import React, { useEffect, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Button, Form, Input, Select, Switch } from "antd";
import { Label } from "../ui";
import { getCountryDialCodes } from "../../apis/organizers";

export default function OrganizerForm({
  initialValues = {
    name: "",
    email: "",
    code: "+91",
    mobile_number: "",
    access_level: 0,
  },
  showAccessToggle = false,
  onSubmit,
  onCancel,
}) {
  const [dialCodesLoading, setDialCodesLoading] = useState(false);
  const [dialCodesFetched, setDialCodesFetched] = useState(false);
  const [dialCodeOptions, setDialCodeOptions] = useState([]);

  const OrganizerSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().optional().email("Invalid email format"),
    mobile_number: Yup.string()
      .required("Mobile number is required")
      .min(10, "Mobile number must be 10 digit"),
    code: Yup.string().required("Dial code is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: OrganizerSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        await onSubmit?.(values);
        resetForm();
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched, getFieldProps, handleSubmit, isSubmitting } =
    formik;

  const fetchDialCodes = async () => {
    if (dialCodesLoading || dialCodesFetched) return;
    setDialCodesLoading(true);
    try {
      const res = await getCountryDialCodes();
      if (res?.status === 200 && res?.data) {
        const countries = res.data?.data;
        if (Array.isArray(countries)) {
          const seen = new Set();
          const mapped = [];
          for (const item of countries) {
            const name = item?.name;
            const flag = item?.flag || "";
            const codeRaw = item?.dial_code;
            const code = String(codeRaw).startsWith("+")
              ? String(codeRaw)
              : `+${String(codeRaw)}`;
            if (!seen.has(code)) {
              seen.add(code);
              mapped.push({
                label: `${flag ? flag + " " : ""}${code} ${name}`.trim(),
                value: code,
              });
            }
          }
          if (mapped.length > 0) setDialCodeOptions(mapped);
        }
      }
      setDialCodesFetched(true);
    } catch (e) {
      console.log(e);
    } finally {
      setDialCodesLoading(false);
    }
  };

  useEffect(() => {
    fetchDialCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormikProvider value={formik}>
      <Form layout="vertical" onFinish={handleSubmit}>
        <Label
          label="Name"
          error={touched.name && errors.name}
          helperText={touched.name && errors.name ? errors.name : ""}
          required={true}
        >
          <Input placeholder="Enter Name" {...getFieldProps("name")} />
        </Label>

        <Label
          label="Email"
          error={touched.email && errors.email}
          helperText={touched.email && errors.email ? errors.email : ""}
        >
          <Input
            placeholder="Enter Email"
            type="email"
            {...getFieldProps("email")}
          />
        </Label>

        <Label
          label="Mobile Number"
          error={touched.mobile_number && errors.mobile_number}
          helperText={
            touched.mobile_number && errors.mobile_number
              ? errors.mobile_number
              : ""
          }
          required={true}
        >
          <div className="d-flex align-items-center gap-2">
            <Select
              showSearch
              placeholder="Code"
              value={values.code}
              onChange={(val) => formik.setFieldValue("code", val)}
              options={dialCodeOptions}
              optionFilterProp="label"
              optionLabelProp="label"
              loading={dialCodesLoading}
              onDropdownVisibleChange={(open) => {
                if (open) fetchDialCodes();
              }}
              style={{ width: "30%" }}
            />
            <Input
              placeholder="Enter Mobile Number"
              {...getFieldProps("mobile_number")}
              style={{ width: "70%" }}
              onInput={(e) => {
                const target = e.target;
                target.value = target.value.replace(/[^0-9]/g, "");
              }}
            />
          </div>
        </Label>

        {showAccessToggle && (
          <div className="d-flex align-items-center gap-2">
            <p className="m-0 fw-bold">Event Privileges</p>
            <Switch
              size="small"
              checked={values?.access_level}
              onChange={(checked) => {
                formik.setFieldValue("access_level", checked ? 1 : 0);
              }}
            />
          </div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={isSubmitting}>
            Submit
          </Button>
        </div>
      </Form>
    </FormikProvider>
  );
}
